import * as React from 'react'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { FormikContextType } from 'formik'
import { FileMutationDto } from '../../../../config/fileConfig'
import { FileSurchargeDto, SurchargeAction } from './FileSurchargeList'
import { Field, Input } from '@fluentui/react-components'
import { SurchargeFormProps } from './FileSurchargeForm'
import { useTranslation } from 'react-i18next'
import { SurchargeValueType } from '../../../../config/i18nConfig'
import useSurchargeType from '../../../../hooks/useSurchargeType'
import SurchargeTypeCombobox from '../../../Combobox/i18n/SurchargeTypeCombobox'
import FormattableNumberInput, { CommitEventType } from '../../../Input/FormattableNumberInput'
import MoneyInput from '../../../Input/MoneyInput'

export type SurchargeFormContainerProps = SurchargeFormProps & {
    fileContext: FormikContextType<FileMutationDto>
    index: number
    action: SurchargeAction
}

export default function FileSurchargeFormContainer(props: SurchargeFormContainerProps): ReactElement {
    const { t: translate } = useTranslation()
    const { values, setFieldValue } = props.fileContext

    const surcharge = useMemo(
        () => (values?.surcharges[props.index] as FileSurchargeDto) ?? props.initialValues,
        [values.surcharges],
    )
    const [selectedSurchargeTypes, setSelectedSurchargeTypes] = useState<string[]>([surcharge?.surchargeType])
    const { surchargeType, surchargeTypeName } = useSurchargeType(surcharge?.surchargeType)

    useEffect(() => {
        if (surchargeType) {
            setFieldValue(`surcharges[${props.index}].name`, surchargeTypeName)
            setFieldValue(`surcharges[${props.index}].value`, surchargeType.value)
        }
    }, [surchargeType])

    return (
        <>
            <Field label={translate('file.edit.form.finance.surcharge.surchargetype')} required>
                <SurchargeTypeCombobox
                    required
                    multiselect={false}
                    selectedOptions={selectedSurchargeTypes}
                    onOptionSelect={(_, data) => {
                        setSelectedSurchargeTypes(data.selectedOptions)
                        setFieldValue(`surcharges[${props.index}].surchargeType`, data.selectedOptions?.[0])
                    }}
                />
            </Field>
            <Field label={translate('file.edit.form.finance.surcharge.name')} required>
                <Input
                    value={surcharge?.name}
                    onChange={(_, data) => {
                        setFieldValue(`surcharges[${props.index}].name`, data.value)
                    }}
                />
            </Field>
            <Field label={translate('file.edit.form.finance.surcharge.value')} required>
                {surchargeType?.valueType === SurchargeValueType.Fixed && (
                    <MoneyInput
                        value={surcharge?.value}
                        allowNegative
                        currency={surchargeType?.currency?.alpha}
                        onCommit={(eventType: CommitEventType, value: string) => {
                            if (eventType !== CommitEventType.OnLoad) {
                                setFieldValue(`surcharges[${props.index}].value`, value)
                            }
                        }}
                    />
                )}
                {surchargeType?.valueType === SurchargeValueType.Percentage && (
                    <FormattableNumberInput
                        decimals={2}
                        allowNegative
                        value={surcharge?.value}
                        symbol={'%'}
                        onCommit={(eventType: CommitEventType, value: string) => {
                            if (eventType !== CommitEventType.OnLoad) {
                                setFieldValue(`surcharges[${props.index}].value`, value)
                            }
                        }}
                    />
                )}
                {surchargeType === undefined && translate('file.edit.form.finance.surcharge.choosetype')}
            </Field>
        </>
    )
}
